import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { motion } from "framer-motion";
import { Calendar } from "../components/ui/calendar";
import { ChevronLeft, ChevronRight, Check, X } from "lucide-react";
import { fr } from "date-fns/locale";

export default function CalendarPage() {
  const { authHeaders } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState([]);
  const [routineHistory, setRoutineHistory] = useState([]);
  const [selectedDayData, setSelectedDayData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const [calendarRes, historyRes] = await Promise.all([
        axios.get(`${API}/routine/calendar`, authHeaders()),
        axios.get(`${API}/routine/history`, authHeaders())
      ]);
      setCalendarData(calendarRes.data);
      setRoutineHistory(historyRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    const dateStr = date.toISOString().split('T')[0];
    const dayData = routineHistory.find(r => r.date === dateStr);
    setSelectedDayData(dayData || null);
  };

  // Create a map of dates to progress for quick lookup
  const dateProgressMap = {};
  calendarData.forEach(item => {
    dateProgressMap[item.date] = item.progress_percent;
  });

  // Custom day content renderer
  const getDayClassName = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    const progress = dateProgressMap[dateStr];
    
    if (progress === undefined) return "";
    if (progress === 100) return "bg-[#A9DFBF] text-[#2C3E50] rounded-full";
    if (progress >= 50) return "bg-[#AED6F1] text-[#2C3E50] rounded-full";
    if (progress > 0) return "bg-[#FAD7A1] text-[#2C3E50] rounded-full";
    return "";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 rounded-full border-4 border-[#A3E4D7]/30 border-t-[#A3E4D7]"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div>
        <h1 className="font-nunito text-2xl font-bold text-[#2C3E50]">
          Historique
        </h1>
        <p className="text-[#7F8C8D]">
          Regarde ton parcours
        </p>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-[#A9DFBF]" />
          <span className="text-sm text-[#7F8C8D]">100%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-[#AED6F1]" />
          <span className="text-sm text-[#7F8C8D]">50%+</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-[#FAD7A1]" />
          <span className="text-sm text-[#7F8C8D]">Commencé</span>
        </div>
      </div>

      {/* Calendar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl p-6 shadow-soft flex justify-center"
      >
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleDateSelect}
          locale={fr}
          data-testid="history-calendar"
          className="rounded-2xl"
          modifiers={{
            completed: (date) => {
              const dateStr = date.toISOString().split('T')[0];
              return dateProgressMap[dateStr] === 100;
            },
            partial: (date) => {
              const dateStr = date.toISOString().split('T')[0];
              const progress = dateProgressMap[dateStr];
              return progress !== undefined && progress >= 50 && progress < 100;
            },
            started: (date) => {
              const dateStr = date.toISOString().split('T')[0];
              const progress = dateProgressMap[dateStr];
              return progress !== undefined && progress > 0 && progress < 50;
            }
          }}
          modifiersClassNames={{
            completed: "bg-[#A9DFBF] text-[#2C3E50] hover:bg-[#A9DFBF]/80",
            partial: "bg-[#AED6F1] text-[#2C3E50] hover:bg-[#AED6F1]/80",
            started: "bg-[#FAD7A1] text-[#2C3E50] hover:bg-[#FAD7A1]/80"
          }}
          components={{
            IconLeft: () => <ChevronLeft className="h-4 w-4" />,
            IconRight: () => <ChevronRight className="h-4 w-4" />
          }}
        />
      </motion.div>

      {/* Selected Day Details */}
      {selectedDayData && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl p-6 shadow-soft"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-nunito font-bold text-[#2C3E50]">
              {new Date(selectedDayData.date).toLocaleDateString('fr-FR', {
                weekday: 'long',
                day: 'numeric',
                month: 'long'
              })}
            </h2>
            <span className="text-lg font-bold text-[#A3E4D7]" data-testid="selected-day-progress">
              {selectedDayData.progress_percent}%
            </span>
          </div>

          <div className="space-y-2">
            {selectedDayData.tasks?.map((task) => (
              <div
                key={task.task_key}
                className="flex items-center gap-3 p-3 rounded-xl bg-[#FAFCFB]"
              >
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                  task.completed ? "bg-[#A9DFBF]" : "bg-[#FAD7A1]/30"
                }`}>
                  {task.completed ? (
                    <Check className="text-[#2C3E50]" size={14} />
                  ) : (
                    <X className="text-[#7F8C8D]" size={14} />
                  )}
                </div>
                <span className={`text-sm ${
                  task.completed ? "text-[#2C3E50]" : "text-[#7F8C8D]"
                }`}>
                  {task.label}
                </span>
              </div>
            ))}
          </div>

          {selectedDayData.quote && (
            <div className="mt-4 p-4 rounded-xl bg-[#FAD7A1]/20">
              <p className="text-sm text-[#2C3E50] italic">
                "{selectedDayData.quote}"
              </p>
            </div>
          )}
        </motion.div>
      )}

      {/* No data message */}
      {!selectedDayData && selectedDate && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-8"
        >
          <p className="text-[#7F8C8D]">
            Pas de données pour ce jour
          </p>
        </motion.div>
      )}

      {/* Stats summary */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-gradient-to-br from-[#A3E4D7]/30 to-[#AED6F1]/30 rounded-3xl p-6"
      >
        <h3 className="font-nunito font-bold text-[#2C3E50] mb-4">
          Résumé
        </h3>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-[#2C3E50]" data-testid="total-days">
              {routineHistory.length}
            </p>
            <p className="text-[#7F8C8D] text-sm">Jours suivis</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-[#2C3E50]" data-testid="perfect-days">
              {calendarData.filter(d => d.progress_percent === 100).length}
            </p>
            <p className="text-[#7F8C8D] text-sm">Jours parfaits</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-[#2C3E50]" data-testid="average-progress">
              {routineHistory.length > 0 
                ? Math.round(routineHistory.reduce((acc, r) => acc + (r.progress_percent || 0), 0) / routineHistory.length)
                : 0}%
            </p>
            <p className="text-[#7F8C8D] text-sm">Moyenne</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
